import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import DataDashboard from '../../../common/DataDashboard';
import AddSupplierModal from '../../modals/AddSupplierModal';
import SupplierApprovalModal from '../../modals/SupplierApprovalModal';
import { AdminUser } from '../../../../types/admin-v2';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import Toast from '../../../../components/Toast';
import { getDynamicSubItems } from '../../../../utils/getDynamicSubItems';
import CompanySubNavigation from '../../../common/CompanySubNavigation';
import { getCategoryName } from '../../../../utils/categories'; // Import category helper

interface CompanySuppliersDashboardProps {
  user: AdminUser;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
}

const CompanySuppliersDashboard: React.FC<CompanySuppliersDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout
}) => {
  const { companyId } = useParams<{ companyId: string }>();
  const { token } = useAuth();
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [companyName, setCompanyName] = useState<string>('');
  const [companyPlan, setCompanyPlan] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false
  });

  // Approval modal state - shows all suppliers as rows
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalQueue, setApprovalQueue] = useState<Array<{ id: string; name: string }>>([]);

  // For selection tracking (used for approval queue)
  const [selectedSupplierIds, setSelectedSupplierIds] = useState<string[]>([]);

  // Document counts by supplier for inline display
  const [documentCounts, setDocumentCounts] = useState<Record<string, { count: number; documents: Array<{ document_id: string; filename: string }> }>>({});

  // Date editing state
  const [editingDates, setEditingDates] = useState<{
    supplierId: string;
    supplierName: string;
    startDate: string;
    endDate: string;
  } | null>(null);
  const [isSavingDates, setIsSavingDates] = useState(false);

  // Define table columns for suppliers (with company code column)
  const supplierColumns: any[] = [
    { key: 'supplier_id', title: 'Global ID', type: 'text' as const, sortable: true, filterable: true, editable: false, width: '100px' },
    { key: 'name', title: 'Name', type: 'text' as const, sortable: true, filterable: true, editable: true, width: '200px' },
    {
      key: 'category',
      title: 'Category',
      type: 'categories' as const,
      sortable: true,
      filterable: true,
      editable: true,
      width: '150px',
      render: (value: any) => {
        // Handle array or single string value
        if (Array.isArray(value)) {
          return value.map(v => getCategoryName(v)).join(', ');
        }
        return getCategoryName(value) || value;
      }
    },
    { key: 'address', title: 'Address', type: 'text' as const, sortable: false, filterable: true, editable: true, width: '250px' },
    { key: 'website', title: 'Website', type: 'text' as const, sortable: false, filterable: true, editable: true, width: '150px' },
    { key: 'online_sales', title: 'Online Sales', type: 'status' as const, sortable: true, filterable: true, editable: true, width: '120px' },
    { key: 'online_marketing', title: 'Online Marketing', type: 'status' as const, sortable: true, filterable: true, editable: true, width: '140px' },
    { key: 'approval_status', title: 'Approval', type: 'status' as const, sortable: true, filterable: true, editable: true, width: '120px' },
    {
      key: 'approval_start_date',
      title: 'Approval Start',
      type: 'text' as const,
      sortable: false,
      filterable: true,
      editable: false,
      width: '120px',
      render: (value: any, row: any) => {
        // Find company specific code entry
        const currentCompanyId = companyId; // From useParams
        const codeEntry = row.company_specific_codes?.find((c: any) => String(c.company_id) === String(currentCompanyId));

        if (!codeEntry?.approval_start_date) return <span style={{ color: '#9ca3af' }}>-</span>;

        const formatDate = (dateStr: string) => {
          if (!dateStr) return '-';
          return new Date(dateStr).toLocaleDateString();
        };

        return (
          <div
            onClick={(e) => {
              if (isEditing) {
                e.stopPropagation();
                setEditingDates({
                  supplierId: row._id || row.id,
                  supplierName: row.name,
                  startDate: codeEntry.approval_start_date?.split('T')[0] || '',
                  endDate: codeEntry.approval_end_date?.split('T')[0] || ''
                });
              }
            }}
            style={{
              fontSize: '12px',
              cursor: isEditing ? 'pointer' : 'default',
              padding: '4px',
              borderRadius: '4px',
              backgroundColor: isEditing ? '#f3f4f6' : 'transparent',
              border: isEditing ? '1px dashed #d1d5db' : 'none',
              color: '#059669',
              fontWeight: 500
            }}
            title={isEditing ? "Click to edit dates" : ""}
          >
            {formatDate(codeEntry.approval_start_date)}
            {isEditing && (
              <div style={{ fontSize: '10px', color: '#3b82f6', marginTop: '2px', textAlign: 'center' }}>
                ✎ Edit
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'approval_end_date',
      title: 'Approval End',
      type: 'text' as const,
      sortable: false,
      filterable: true,
      editable: false,
      width: '120px',
      render: (value: any, row: any) => {
        // Find company specific code entry
        const currentCompanyId = companyId; // From useParams
        const codeEntry = row.company_specific_codes?.find((c: any) => String(c.company_id) === String(currentCompanyId));

        if (!codeEntry?.approval_end_date) return <span style={{ color: '#9ca3af' }}>-</span>;

        const formatDate = (dateStr: string) => {
          if (!dateStr) return '-';
          return new Date(dateStr).toLocaleDateString();
        };

        return (
          <div
            onClick={(e) => {
              if (isEditing) {
                e.stopPropagation();
                setEditingDates({
                  supplierId: row._id || row.id,
                  supplierName: row.name,
                  startDate: codeEntry.approval_start_date?.split('T')[0] || '',
                  endDate: codeEntry.approval_end_date?.split('T')[0] || ''
                });
              }
            }}
            style={{
              fontSize: '12px',
              cursor: isEditing ? 'pointer' : 'default',
              padding: '4px',
              borderRadius: '4px',
              backgroundColor: isEditing ? '#f3f4f6' : 'transparent',
              border: isEditing ? '1px dashed #d1d5db' : 'none',
              color: '#dc2626',
              fontWeight: 500
            }}
            title={isEditing ? "Click to edit dates" : ""}
          >
            {formatDate(codeEntry.approval_end_date)}
            {isEditing && (
              <div style={{ fontSize: '10px', color: '#3b82f6', marginTop: '2px', textAlign: 'center' }}>
                ✎ Edit
              </div>
            )}
          </div>
        );
      }
    },
    // Documents column - shows files for approved suppliers, edit mode has add/delete
    {
      key: 'documents',
      title: 'Documents',
      type: 'text' as const,
      sortable: false,
      filterable: true,
      editable: false,
      width: '160px',
      render: (value: any, row: any) => {
        const supplierId = row._id || row.id;
        const docInfo = documentCounts[supplierId];
        const hasFiles = docInfo && docInfo.count > 0;

        // Only show for approved suppliers
        if (row.approval_status !== 'approved') {
          return <span style={{ color: '#d1d5db' }}>-</span>;
        }

        // Delete handler
        const handleDelete = async (documentId: string) => {
          if (!token || !window.confirm('Delete this file?')) return;
          try {
            const res = await fetch(
              `${API_ENDPOINTS.BASE_URL}/v2/documents/${documentId}`,
              {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
              }
            );
            if (res.ok) {
              loadDocumentCounts();
            }
          } catch (err) {
            console.error('Delete error:', err);
          }
        };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {/* File list */}
            {hasFiles && docInfo.documents.map((doc) => (
              <div
                key={doc.document_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '6px'
                }}
              >
                <a
                  href={`${API_ENDPOINTS.BASE_URL}/v2/documents/${doc.document_id}/download`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#3b82f6',
                    textDecoration: 'underline',
                    fontSize: '11px',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  onClick={(e) => e.stopPropagation()}
                  title={doc.filename}
                >
                  {doc.filename.length > 12 ? doc.filename.slice(0, 10) + '...' : doc.filename}
                </a>
                {/* Delete button - only in edit mode */}
                {isEditing && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(doc.document_id);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ef4444',
                      cursor: 'pointer',
                      padding: '0 2px',
                      fontSize: '12px'
                    }}
                    title="Delete"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}

            {!hasFiles && <span style={{ color: '#9ca3af', fontSize: '11px' }}>No files</span>}

            {/* Add button - only in edit mode */}
            {isEditing && (
              <label style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                color: '#059669',
                fontSize: '11px',
                cursor: 'pointer',
                padding: '2px 0'
              }}>
                <span>+ Add file</span>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.png"
                  style={{ display: 'none' }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={async (e) => {
                    e.stopPropagation();
                    const file = e.target.files?.[0];
                    if (file && token && companyId) {
                      const formData = new FormData();
                      formData.append('file', file);
                      formData.append('document_type', 'approval_document');

                      try {
                        const res = await fetch(
                          `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/suppliers/${supplierId}/documents`,
                          {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}` },
                            body: formData
                          }
                        );
                        if (res.ok) {
                          loadDocumentCounts();
                        }
                      } catch (err) {
                        console.error('Upload error:', err);
                      }
                    }
                    e.target.value = '';
                  }}
                />
              </label>
            )}
          </div>
        );
      }
    },
    // Company code column (dynamic based on companyId)
    {
      key: `company_code_${companyId}`,
      title: `${companyName} Code`,
      type: 'text' as const,
      sortable: false,
      filterable: true,
      editable: true,
      width: '150px',
      render: (value: any, row: any) => {
        const companyCode = row.company_specific_codes?.find(
          (c: any) => c.company_id === companyId
        );
        return companyCode?.code || '';
      }
    },
    { key: 'overall_score', title: 'Overall Score', type: 'score' as const, sortable: true, filterable: true, editable: false, width: '120px' },
    { key: 'trustpilot_score', title: 'Trustpilot', type: 'score' as const, sortable: true, filterable: true, editable: true, width: '100px' },
    { key: 'google_maps_score', title: 'Google Maps', type: 'score' as const, sortable: true, filterable: true, editable: true, width: '100px' },
    { key: 'mebsis_score', title: 'Mebsis', type: 'score' as const, sortable: true, filterable: true, editable: true, width: '80px' },
    { key: 'email_score', title: 'Email Score', type: 'score' as const, sortable: true, filterable: true, editable: true, width: '100px' },
    { key: 'email_score', title: 'Email Score', type: 'score' as const, sortable: true, filterable: true, editable: true, width: '100px' },
    // Status column - only for top-level admins
    ...(user.role === 'admin' ? [{ key: 'status', title: 'Status', type: 'status' as const, sortable: true, filterable: true, editable: false, width: '100px' }] : []),
    { key: 'region', title: 'Region', type: 'text' as const, sortable: true, filterable: true, editable: false, width: '80px' }
  ];

  // Load company info
  const loadCompanyInfo = async () => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setCompanyName(data.data.name || 'Company');
          setCompanyPlan(data.data.plan || '');
        }
      }
    } catch (error) {
      console.error('Error loading company info:', error);
    }
  };

  // Load suppliers data
  const loadSuppliers = async (page = 1, limit = 25, search = '', sortBy = 'supplier_id', sortOrder = 'asc', columnFilters: Record<string, string> = {}) => {
    if (!token) {
      setError('No authentication token found');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_by: sortBy || 'supplier_id',
        sort_order: sortOrder || 'asc'
      });

      if (search) {
        queryParams.append('search', search);
      }

      // Add column filters (suppliers API uses direct field names, not filter_ prefix)
      Object.entries(columnFilters).forEach(([key, value]) => {
        if (value && value.toString().trim()) {
          queryParams.append(key, value.toString().trim());
        }
      });

      console.log('🔍 COMPANY SUPPLIERS DEBUG - Fetching with params:', {
        page,
        limit,
        search,
        sortBy,
        sortOrder,
        columnFilters
      });

      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/suppliers?${queryParams.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      console.log('🔍 COMPANY SUPPLIERS DEBUG - Backend response:', data);
      console.log('🔍 COMPANY SUPPLIERS DEBUG - Supplier count:', data.data?.length);

      if (data.success) {
        // Transform suppliers data for DataDashboard
        const transformedSuppliers = (data.data || []).map((supplier: any) => {
          // Map categories array to category field for DataDashboard
          const categoriesArray = supplier.categories || (supplier.category ? [supplier.category] : []);

          console.log('🔍 CATEGORY DEBUG - Supplier:', supplier.name, 'categories:', supplier.categories, 'category:', supplier.category, 'mapped:', categoriesArray);

          // Extract company-specific approval status for this company
          const companyCode = (supplier.company_specific_codes || []).find(
            (c: any) => String(c.company_id) === String(companyId)
          );
          const approvalStatus = companyCode?.approval_status || 'pending';
          const approvalStartDate = companyCode?.approval_start_date || null;
          const approvalEndDate = companyCode?.approval_end_date || null;

          return {
            ...supplier,
            id: supplier._id,
            global_id: supplier.supplier_id,
            // Map categories array to category field for DataDashboard (categories type expects array)
            category: categoriesArray,
            categories: categoriesArray,
            company_specific_codes: supplier.company_specific_codes || [],
            // Add top-level approval fields for DataDashboard columns and filtering
            approval_status: approvalStatus,
            approval_start_date: approvalStartDate,
            approval_end_date: approvalEndDate
          };
        });

        console.log('🔍 COMPANY SUPPLIERS DEBUG - Transformed suppliers:', transformedSuppliers);

        setSuppliers(transformedSuppliers);
        setPaginationInfo({
          page: data.pagination?.page || page,
          limit: data.pagination?.limit || limit,
          total: data.pagination?.total || transformedSuppliers.length,
          pages: data.pagination?.pages || Math.ceil((data.pagination?.total || transformedSuppliers.length) / limit),
          has_next: data.pagination?.has_next || false,
          has_prev: data.pagination?.has_prev || false
        });
      } else {
        throw new Error(data.message || 'Failed to load suppliers');
      }
    } catch (error: any) {
      console.error('Error loading suppliers:', error);
      setError(error.message || 'Failed to load suppliers');
      setSuppliers([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load document counts for inline display in table
  const loadDocumentCounts = async () => {
    if (!token || !companyId) return;

    try {
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/suppliers/documents/counts`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      if (data.success) {
        setDocumentCounts(data.data || {});
      }
    } catch (error) {
      console.error('Error loading document counts:', error);
    }
  };

  // Handle data fetch from DataDashboard
  const handleDataFetch = (params: { page: number; limit: number; search?: string; sortBy?: string; sortOrder?: string; columnFilters?: Record<string, string> }) => {
    // Store current filter state
    setCurrentSearch(params.search || '');
    setCurrentSortBy(params.sortBy || 'supplier_id');
    setCurrentSortOrder((params.sortOrder as 'asc' | 'desc') || 'asc');
    setCurrentColumnFilters(params.columnFilters || {});

    loadSuppliers(params.page, params.limit, params.search || '', params.sortBy || 'supplier_id', params.sortOrder || 'asc', params.columnFilters || {});
  };

  // Handle edit toggle
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  // Handle edit supplier
  const handleEditSupplier = async (id: string, field: string, value: any, row?: any) => {
    try {
      console.log('🔧 EDIT SUPPLIER - id:', id, 'field:', field, 'value:', value);

      // Handle approval_status field
      if (field === 'approval_status') {
        // If changing to 'approved', add to queue for document upload
        if (value === 'approved') {
          // Find the supplier to get its name
          const supplier = suppliers.find(s => String(s._id) === String(id) || String(s.id) === String(id));
          if (supplier) {
            // Add to queue if not already there
            setApprovalQueue(prev => {
              const exists = prev.some(s => s.id === id);
              if (!exists) {
                return [...prev, { id, name: supplier.name }];
              }
              return prev;
            });
            // Open modal if not already open
            if (!showApprovalModal) {
              setShowApprovalModal(true);
            }
          }
          return; // Don't call API directly, modal will handle it
        }

        // For 'rejected' or 'pending', call API directly with correct endpoint
        const endpoint = value === 'rejected' ? 'reject' : 'pending';
        const response = await fetch(
          `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/suppliers/${id}/${endpoint}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: value === 'rejected' ? JSON.stringify({ reason: '' }) : undefined
          }
        );

        const data = await response.json();
        if (data.success) {
          // Update local state using functional setState for concurrent updates
          setSuppliers(prev => prev.map(s => {
            if (String(s._id) === String(id) || String(s.id) === String(id)) {
              const updatedCodes = (s.company_specific_codes || []).map((c: any) => {
                if (String(c.company_id) === String(companyId)) {
                  return { ...c, approval_status: value };
                }
                return c;
              });
              return { ...s, company_specific_codes: updatedCodes, approval_status: value };
            }
            return s;
          }));
          setToastMessage(`Supplier ${value} successfully`);
          setToastType('success');
          setShowToast(true);
        } else {
          throw new Error(data.error || `Failed to ${endpoint} supplier`);
        }
        return;
      }

      // Check if editing a company code field
      if (field.startsWith('company_code_')) {
        const editCompanyId = field.replace('company_code_', '');

        // Find the supplier with proper string comparison
        console.log('🔍 DEBUG - Looking for supplier with id:', id, 'in suppliers:', suppliers.map(s => ({ id: s.id, _id: s._id })));
        const supplier = suppliers.find(s => String(s.id) === String(id) || String(s._id) === String(id));
        if (!supplier) {
          console.error('Supplier not found for id:', id);
          return;
        }

        // Update company_specific_codes array
        let companyCodes = supplier.company_specific_codes || [];
        const existingCodeIndex = companyCodes.findIndex((c: any) => c.company_id === editCompanyId);

        if (existingCodeIndex >= 0) {
          // Update existing code
          companyCodes[existingCodeIndex].code = value;
        } else {
          // Add new code entry
          companyCodes.push({
            company_id: editCompanyId,
            company_name: companyName,
            code: value
          });
        }

        // Send update to backend
        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/suppliers/${id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ company_specific_codes: companyCodes })
        });

        if (!response.ok) {
          throw new Error('Failed to update supplier');
        }

        // Update local state using functional setState for concurrent updates
        setSuppliers(prev => prev.map(s => {
          if (String(s.id) === String(id) || String(s._id) === String(id)) {
            return { ...s, company_specific_codes: companyCodes };
          }
          return s;
        }));

        console.log('✅ Company code updated successfully');
        return;
      }

      // Handle other field edits
      console.log('Editing other field:', field, value);

      // Send update to backend
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/suppliers/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [field]: value })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update supplier (HTTP ${response.status})`);
      }

      const result = await response.json();

      if (result.success) {
        // Update local state using functional setState for concurrent updates
        setSuppliers(prev => prev.map(s => {
          if (String(s.id) === String(id) || String(s._id) === String(id)) {
            return { ...s, [field]: value, updated_at: new Date().toISOString() };
          }
          return s;
        }));
        console.log('✅ Supplier field updated successfully');
      } else {
        throw new Error(result.error || 'Failed to update supplier');
      }
    } catch (error) {
      console.error('Error editing supplier:', error);
      throw error; // Re-throw to let DataDashboard handle the error
    }
  };

  // Handle delete suppliers - show confirmation modal
  const handleDeleteSuppliers = (ids: string[]) => {
    setSuppliersToDelete(ids);
    setShowDeleteConfirm(true);
  };

  // Confirm delete handler
  const handleConfirmDelete = async () => {
    try {
      if (!token || !companyId) {
        throw new Error('Missing authentication token or company ID');
      }

      // If user is top-level admin, delete suppliers completely
      // Otherwise, just remove company codes
      if (isTopLevelAdmin) {
        // Delete suppliers completely
        const response = await fetch(`${API_ENDPOINTS.SUPPLIERS_V2}/bulk-delete`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            supplier_ids: suppliersToDelete,
            region: 'tr' // Default region for now, could be made dynamic
            // No company_id needed for top-level admin
          })
        });

        if (!response.ok) {
          let errorMessage = `Failed to delete suppliers (HTTP ${response.status})`;

          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch (parseError) {
            errorMessage = response.statusText || errorMessage;
          }

          throw new Error(errorMessage);
        }

        const result = await response.json();

        if (result.success) {
          setShowDeleteConfirm(false);
          setSuppliersToDelete([]);

          // Reload suppliers with current filters preserved
          await loadSuppliers(
            paginationInfo.page,
            paginationInfo.limit,
            currentSearch,
            currentSortBy,
            currentSortOrder,
            currentColumnFilters
          );

          setToastMessage(`${result.deleted_count} supplier(s) deleted successfully`);
          setToastType('success');
          setShowToast(true);
        } else {
          const errorMessage = result.error || result.message || 'Failed to delete suppliers';
          throw new Error(errorMessage);
        }
      } else {
        // For user admins, remove company codes from suppliers
        if (!companyId) {
          setShowDeleteConfirm(false);
          setToastMessage('Company ID is required to remove company codes');
          setToastType('error');
          setShowToast(true);
          return;
        }

        const response = await fetch(`${API_ENDPOINTS.SUPPLIERS_V2}/bulk-delete`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            supplier_ids: suppliersToDelete,
            company_id: companyId, // Include company_id to remove only company codes
            region: 'tr' // Default region for now, could be made dynamic
          })
        });

        if (!response.ok) {
          let errorMessage = `Failed to remove company codes (HTTP ${response.status})`;

          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch (parseError) {
            errorMessage = response.statusText || errorMessage;
          }

          throw new Error(errorMessage);
        }

        const result = await response.json();

        if (result.success) {
          setShowDeleteConfirm(false);
          setSuppliersToDelete([]);

          // Reload suppliers with current filters preserved
          await loadSuppliers(
            paginationInfo.page,
            paginationInfo.limit,
            currentSearch,
            currentSortBy,
            currentSortOrder,
            currentColumnFilters
          );

          setToastMessage(`Successfully removed company codes from ${result.removed_count || suppliersToDelete.length} supplier(s)`);
          setToastType('success');
          setShowToast(true);
        } else {
          const errorMessage = result.error || result.message || 'Failed to remove company codes from suppliers';
          throw new Error(errorMessage);
        }
      }
    } catch (error) {
      console.error('Error deleting suppliers:', error);
      setShowDeleteConfirm(false);
      setToastMessage(`Failed to delete suppliers: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setToastType('error');
      setShowToast(true);
    }
  };

  // Handle export
  const handleExport = async (exportData: any) => {
    try {
      if (!token || !companyId) {
        throw new Error('No authentication token or company ID found');
      }

      console.log('🚀 CompanySuppliersDashboard handleExport called with:', exportData);

      // Company-specific suppliers export için filters'ı düzenle
      // Backend'de suppliers collection'ında company_id field'ı yok
      // Company suppliers için company_specific_codes array'inde company_id var
      // MongoDB'de array içinde filtreleme için $elemMatch kullanmalıyız
      // Backend'de company_id string olarak saklanıyor (ObjectId değil)
      // Backend company suppliers endpoint'i hem company_specific_codes hem de recommended_by_companies için $or kullanıyor
      // Backend export endpoint'i artık regional collection'ları kontrol ediyor (tr, en, de)

      const companyFilter = {
        $or: [
          {
            company_specific_codes: {
              $elemMatch: {
                company_id: companyId
              }
            }
          },
          {
            recommended_by_companies: {
              $elemMatch: {
                company_id: companyId
              }
            }
          }
        ]
      };

      // Eğer seçili item'lar varsa, $and ile birleştir
      if (exportData.filters?._id?.$in && exportData.filters._id.$in.length > 0) {
        const baseFilters = {
          $and: [
            companyFilter,
            { _id: exportData.filters._id }
          ]
        };
        var exportPayload = {
          ...exportData,
          filters: baseFilters
        };
      } else {
        var exportPayload = {
          ...exportData,
          filters: companyFilter
        };
      }

      console.log('🚀 CompanySuppliersDashboard export payload:', exportPayload);

      // ExportModal'dan gelen data yapısını kullan, company_id filtresi ile
      const response = await fetch(API_ENDPOINTS.EXPORT_DATA, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(exportPayload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🚀 CompanySuppliersDashboard export error response:', errorText);
        throw new Error(`Export failed: ${response.status} ${errorText}`);
      }

      // Handle file download
      const blob = await response.blob();
      console.log('🚀 CompanySuppliersDashboard export blob size:', blob.size);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      // Format: isim_YYYYMMDD
      const today = new Date();
      const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
      const baseFilename = exportData.filename || `company_${companyId}_suppliers`;
      a.download = `${baseFilename}_${dateStr}.${exportData.format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('🚀 CompanySuppliersDashboard export completed successfully');
    } catch (error) {
      console.error('🚀 CompanySuppliersDashboard export error:', error);
      alert(`Failed to export suppliers: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Handle supplier added successfully (refresh list)
  const handleSupplierAdded = () => {
    loadSuppliers();
  };

  // Load initial data
  useEffect(() => {
    if (companyId) {
      loadCompanyInfo();
      loadSuppliers();
      loadDocumentCounts();
    }
  }, [companyId]);

  // Delete confirmation states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [suppliersToDelete, setSuppliersToDelete] = useState<string[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  // Store current filter/search/sort state for reloading after delete
  const [currentSearch, setCurrentSearch] = useState<string>('');
  const [currentSortBy, setCurrentSortBy] = useState<string>('supplier_id');
  const [currentSortOrder, setCurrentSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentColumnFilters, setCurrentColumnFilters] = useState<Record<string, string>>({});

  // Only show secondary sidebar for admin users, not for regular users
  const isAdmin = user.role === 'admin';

  // Check if user is a top-level admin (role=admin, no company_id)
  const isTopLevelAdmin = user.role === 'admin' && !user.company_id && !user.companyId;

  // Columns to hide for basic plan users
  const basicPlanHiddenColumns = [
    'online_sales',
    'online_marketing',
    'overall_score',
    'trustpilot_score',
    'google_maps_score',
    'mebsis_score',
    'email_score'
  ];

  // Filter columns based on company plan
  const filteredColumns = useMemo(() => {
    if (companyPlan === 'basic' || companyPlan === 'pro') {
      return supplierColumns.filter(col => !basicPlanHiddenColumns.includes(col.key));
    }
    return supplierColumns;
  }, [companyPlan, supplierColumns]);

  return (
    <div className="admin-v2-dashboard-container">
      <DataDashboard
        title={`${companyName} Suppliers`}
        pageDescription="Manage your approved suppliers list"
        collection="suppliers"
        data={suppliers}
        columns={filteredColumns}
        user={user}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        onDataChange={setSuppliers}
        onDataFetch={handleDataFetch}
        layoutType={isAdmin ? 'admin' : 'user'}
        onEdit={handleEditSupplier}
        onEditToggle={handleEditToggle}
        isEditing={isEditing}
        onDelete={handleDeleteSuppliers}
        onExport={handleExport}
        paginationInfo={paginationInfo}
        showAddButton={true}
        showSearch={true}
        showSelection={true}
        getDynamicSubItems={getDynamicSubItems}
        onSelectionChange={(ids) => setSelectedSupplierIds(ids)}
        AddModal={(props) => (
          <AddSupplierModal
            {...props}
            user={user}
            companyId={companyId}
            companyName={companyName}
            onSuccess={handleSupplierAdded}
            isBasicUser={companyPlan === 'basic'}
          />
        )}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setSuppliersToDelete([]);
        }}
        onConfirm={handleConfirmDelete}
        title={isTopLevelAdmin ? "Delete Suppliers" : "Remove Company Codes"}
        message={
          isTopLevelAdmin
            ? `Are you sure you want to delete ${suppliersToDelete.length} supplier(s)? This action cannot be undone.`
            : `Are you sure you want to remove company codes from ${suppliersToDelete.length} supplier(s)?`
        }
        confirmText={isTopLevelAdmin ? "Delete" : "Remove"}
        cancelText="Cancel"
        type="danger"
      />

      {/* Supplier Approval Modal */}
      {approvalQueue.length > 0 && showApprovalModal && (
        <SupplierApprovalModal
          isOpen={showApprovalModal}
          onClose={() => {
            setShowApprovalModal(false);
            setApprovalQueue([]);
          }}
          onSuccess={(approvedIds: string[]) => {
            // Update local state for all approved suppliers
            setSuppliers(prev => prev.map(s => {
              const sid = String(s._id || s.id);
              if (approvedIds.includes(sid)) {
                const updatedCodes = (s.company_specific_codes || []).map((c: any) => {
                  if (String(c.company_id) === String(companyId)) {
                    return { ...c, approval_status: 'approved' };
                  }
                  return c;
                });
                return { ...s, company_specific_codes: updatedCodes, approval_status: 'approved' };
              }
              return s;
            }));
            // Refresh document counts to show new uploads
            loadDocumentCounts();
            setToastMessage(`${approvedIds.length} supplier(s) approved successfully`);
            setToastType('success');
            setShowToast(true);
          }}
          suppliers={approvalQueue}
          companyId={companyId || ''}
          companyName={companyName}
        />
      )}

      {/* Date Edit Modal */}
      {editingDates && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setEditingDates(null)}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            width: '400px',
            maxWidth: '90%'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Edit Approval Dates</h3>
            <p style={{ marginBottom: '20px', color: '#666' }}>{editingDates.supplierName}</p>

            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Start Date</label>
              <input
                type="date"
                value={editingDates.startDate}
                onChange={e => setEditingDates(prev => prev ? ({ ...prev, startDate: e.target.value }) : null)}
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>End Date</label>
              <input
                type="date"
                value={editingDates.endDate}
                onChange={e => setEditingDates(prev => prev ? ({ ...prev, endDate: e.target.value }) : null)}
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => setEditingDates(null)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
                disabled={isSavingDates}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!companyId || !editingDates.supplierId) return;
                  setIsSavingDates(true);
                  try {
                    const res = await fetch(
                      `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/suppliers/${editingDates.supplierId}`,
                      {
                        method: 'PATCH',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                          approval_start_date: editingDates.startDate,
                          approval_end_date: editingDates.endDate
                        })
                      }
                    );

                    if (res.ok) {
                      setEditingDates(null);
                      window.location.reload();
                    } else {
                      alert('Failed to update dates');
                    }
                  } catch (err) {
                    console.error(err);
                    alert('Error updating dates');
                  } finally {
                    setIsSavingDates(false);
                  }
                }}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  cursor: isSavingDates ? 'not-allowed' : 'pointer',
                  opacity: isSavingDates ? 0.7 : 1
                }}
                disabled={isSavingDates}
              >
                {isSavingDates ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
};

export default CompanySuppliersDashboard;
